import {useSelector} from 'react-redux';
import {useIsAccountingAvailable} from './useIsAccountingAvailable';

/**
 * Whether the company's accounting mode is known yet.
 *
 * `useIsAccountingExportMode` defaults to false while the settings are in flight, which means a
 * Manual ERP company renders as NetSuite for a beat and then switches. That was written down as
 * acceptable and it was not: on staging the pilot client saw the navigation and tabs come up
 * branded with a competitor's product name before correcting themselves, and reported it as a bug
 * (H1MVP-4791). A brief absence is honest; the wrong ERP's name is not.
 *
 * ⚠ Resolves on FAILURE as well as success — see `getCompanySettings`. Gating on "the data arrived"
 * alone would let a single failed request hide the accounting section from a NetSuite company for
 * the whole session, which is a much worse outcome than the flash this prevents.
 *
 * True when accounting is unavailable, because then there is nothing to wait for.
 *
 * @returns {boolean} True once the mode can be trusted.
 *
 * @example
 * const isResolved = useIsAccountingModeResolved();
 * // render the accounting section only when isResolved
 */
export const useIsAccountingModeResolved = () => {
  const isAccountingAvailable = useIsAccountingAvailable();
  const isResolved = useSelector(state => state.accounting?.companySettingsResolved === true);

  return !isAccountingAvailable || isResolved;
};
